/**
 * Auto-Escalation service — wraps `/programs/{programId}/ae-escalation/*`.
 */
import { apiClient } from '@/modules/shared/services/apiClient';
import type { ApiResponse } from '@/modules/shared/types';
import type {
  AeEscalationRuleDto,
  CreateAeEscalationRuleDto,
  UpdateAeEscalationRuleDto,
  AeEscalationHistoryListDto,
  AeEscalationHistoryFilter,
} from '../types/pharmacovigilance';

const base = (programId: string) => `/programs/${programId}/ae-escalation`;

export const aeEscalationService = {
  // ── Rules ───────────────────────────────────────────────────
  async listRules(programId: string): Promise<AeEscalationRuleDto[]> {
    const res = await apiClient.get<ApiResponse<AeEscalationRuleDto[]>>(`${base(programId)}/rules`);
    return res.data.data ?? [];
  },

  async getRule(programId: string, ruleId: string): Promise<AeEscalationRuleDto> {
    const res = await apiClient.get<ApiResponse<AeEscalationRuleDto>>(`${base(programId)}/rules/${ruleId}`);
    return res.data.data!;
  },

  async createRule(programId: string, payload: CreateAeEscalationRuleDto): Promise<AeEscalationRuleDto> {
    const res = await apiClient.post<ApiResponse<AeEscalationRuleDto>>(`${base(programId)}/rules`, payload);
    return res.data.data!;
  },

  async updateRule(programId: string, ruleId: string, payload: UpdateAeEscalationRuleDto): Promise<AeEscalationRuleDto> {
    const res = await apiClient.put<ApiResponse<AeEscalationRuleDto>>(`${base(programId)}/rules/${ruleId}`, payload);
    return res.data.data!;
  },

  async toggleRule(programId: string, ruleId: string, enabled: boolean): Promise<void> {
    await apiClient.post(`${base(programId)}/rules/${ruleId}/toggle?enabled=${enabled}`);
  },

  async deleteRule(programId: string, ruleId: string): Promise<void> {
    await apiClient.delete(`${base(programId)}/rules/${ruleId}`);
  },

  // ── Manual evaluation ───────────────────────────────────────
  async evaluate(programId: string): Promise<{ escalated: number }> {
    const res = await apiClient.post<ApiResponse<{ escalated: number }>>(`${base(programId)}/evaluate`);
    return res.data.data ?? { escalated: 0 };
  },

  // ── History ─────────────────────────────────────────────────
  async listHistory(programId: string, filter: AeEscalationHistoryFilter = {}): Promise<AeEscalationHistoryListDto> {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v != null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    const res = await apiClient.get<ApiResponse<AeEscalationHistoryListDto>>(
      `${base(programId)}/history${qs ? `?${qs}` : ''}`,
    );
    return res.data.data ?? { items: [], totalCount: 0, page: 1, pageSize: 20 };
  },

  async resolve(programId: string, historyId: string, resolutionNotes: string, dismiss = false): Promise<void> {
    await apiClient.post(`${base(programId)}/history/${historyId}/resolve`, {
      resolutionNotes,
      dismiss,
    });
  },
};
